// src/hooks/useCollectionDetail.ts
//
// Reads everything needed to render a collection detail view:
//   1. The collection root doc (title, tagline, description, cover) —
//      one-shot getDoc; this almost never changes during a session.
//   2. The collection items subcollection — one-shot list; ordered
//      by order_hint so the UI reflects the collection's intended
//      sequence.
//   3. The user's finds subcollection (live) — onSnapshot so the
//      green checkmarks appear in real-time as the user takes new
//      photos that match.
//
// The collection's path lives on the user's CollectionProgress doc
// as `collection_ref_path` — either '/collections/{id}' (canonical)
// or '/users/{uid}/personal_collections/{id}' (personal). We parse
// it once and read from the right place.
//
// Empty/error fallback: returns an empty items list and a null
// detail object. Caller renders the cover from progress.collection_cover_image_url
// either way; this hook is for the *items grid*, not for the page
// itself.

import { useEffect, useRef, useState } from 'react';
import {
  doc,
  getDoc,
  collection as fsCollection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { auth, db } from '../services/firebase';

export interface CollectionDetailItem {
  id: string;
  title: string;
  description?: string;
  cover_image_url?: string;
  cover_image_thumb?: string;
  reveal_hint?: string;
  order_hint: number;
  lat?: number | null;
  lng?: number | null;
  place_id?: string | null;
  /** Filled in from the user's finds subcollection. Null when
   *  the user hasn't found this item yet. */
  found_at: Date | null;
  found_id?: string;
  /** Photo the user took when finding this item — preferred over
   *  cover_image_url for the circle tile of a found item. */
  found_photo_reference?: string;
}

export interface CollectionDetailDoc {
  /** From the root doc. The progress record also has these but
   *  the root tends to have the longer description text. */
  title: string;
  tagline?: string;
  description?: string;
  cover_image_url?: string;
}

interface UseCollectionDetailReturn {
  detail: CollectionDetailDoc | null;
  items: CollectionDetailItem[];
  isLoading: boolean;
  error: string | null;
}

const tsToDate = (v: any): Date | null => {
  if (!v) return null;
  if (v instanceof Timestamp) return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (typeof v === 'number') return new Date(v);
  return null;
};

/** Parse `/collections/{id}` or `/users/{uid}/personal_collections/{id}`
 *  into the segments getDoc/collection() expects. Returns null on
 *  unrecognized shape. */
const parseCollectionPath = (
  path: string
): { rootSegments: string[]; itemsSegments: string[] } | null => {
  // Strip leading / and trailing / for safety
  const clean = path.replace(/^\/+|\/+$/g, '');
  const segs = clean.split('/');
  // Canonical: collections/{id}
  if (segs.length === 2 && segs[0] === 'collections') {
    return {
      rootSegments: segs,
      itemsSegments: [...segs, 'items'],
    };
  }
  // Personal: users/{uid}/personal_collections/{id}
  if (
    segs.length === 4 &&
    segs[0] === 'users' &&
    segs[2] === 'personal_collections'
  ) {
    return {
      rootSegments: segs,
      itemsSegments: [...segs, 'items'],
    };
  }
  return null;
};

/** Fetch a collection's detail + items + the user's finds.
 *  Pass null/undefined to disable (returns empty state). */
export const useCollectionDetail = (
  collectionId: string | null,
  collectionRefPath: string | null
): UseCollectionDetailReturn => {
  const [detail, setDetail] = useState<CollectionDetailDoc | null>(null);
  const [items, setItems] = useState<CollectionDetailItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const findsUnsubRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Cleanup on disable
    if (!collectionId || !collectionRefPath) {
      findsUnsubRef.current?.();
      findsUnsubRef.current = null;
      setDetail(null);
      setItems([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setError('not_authenticated');
      setIsLoading(false);
      return;
    }

    const parsed = parseCollectionPath(collectionRefPath);
    if (!parsed) {
      setError(`unrecognized collection_ref_path: ${collectionRefPath}`);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const loadStaticParts = async () => {
      try {
        // Root doc — title, tagline, description, cover. One-shot
        // read; collection metadata is stable within a session.
        const [head, ...rest] = parsed.rootSegments;
        const rootRef = doc(db, head, ...rest);
        const rootSnap = await getDoc(rootRef);
        if (cancelled) return;
        if (rootSnap.exists()) {
          const d = rootSnap.data() as any;
          setDetail({
            title: d.title || '',
            tagline: d.tagline,
            description: d.description,
            cover_image_url: d.cover_image_url,
          });
        }

        // Items subcollection — list, ordered.
        const [iHead, ...iRest] = parsed.itemsSegments;
        const itemsRef = fsCollection(db, iHead, ...iRest);
        const itemsQuery = query(itemsRef, orderBy('order_hint', 'asc'));
        const itemsSnap = await getDocs(itemsQuery);
        if (cancelled) return;

        const list: CollectionDetailItem[] = itemsSnap.docs.map((d) => {
          const data = d.data() as any;
          const loc = data.location;
          // GeoPoint serializes as {_latitude, _longitude} on
          // read in some SDK versions; admin SDK Firestore returns
          // {latitude, longitude}. Handle both.
          let lat: number | null = null;
          let lng: number | null = null;
          if (loc) {
            lat =
              typeof loc.latitude === 'number'
                ? loc.latitude
                : typeof loc._latitude === 'number'
                  ? loc._latitude
                  : null;
            lng =
              typeof loc.longitude === 'number'
                ? loc.longitude
                : typeof loc._longitude === 'number'
                  ? loc._longitude
                  : null;
          }
          return {
            id: d.id,
            title: data.title || '',
            description: data.description,
            cover_image_url: data.cover_image_url,
            cover_image_thumb: data.cover_image_thumb,
            reveal_hint: data.reveal_hint,
            order_hint: typeof data.order_hint === 'number' ? data.order_hint : 9999,
            lat,
            lng,
            place_id: data.place_id || null,
            found_at: null,
          };
        });
        setItems(list);
      } catch (err) {
        console.error('useCollectionDetail static load failed:', err);
        if (!cancelled) {
          setError(String((err as any)?.message || err));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    // Live listener on finds — surfaces "you just found one"
    // updates in real-time without forcing a parent re-render.
    findsUnsubRef.current?.();
    const findsRef = fsCollection(
      db,
      'users',
      user.uid,
      'collection_progress',
      collectionId,
      'finds'
    );
    findsUnsubRef.current = onSnapshot(
      findsRef,
      (snap) => {
        if (cancelled) return;
        // Build a map item_id → find data so the upsert into
        // `items` is O(N).
        const findByItemId = new Map<string, { found_at: Date | null; found_id: string; found_photo_reference?: string }>();
        snap.forEach((d) => {
          const data = d.data() as any;
          if (!data.item_id) return;
          findByItemId.set(data.item_id, {
            found_at: tsToDate(data.found_at),
            found_id: d.id,
            found_photo_reference: data.photo_reference,
          });
        });
        setItems((prev) =>
          prev.map((item) => {
            const f = findByItemId.get(item.id);
            if (!f) return { ...item, found_at: null, found_id: undefined, found_photo_reference: undefined };
            return {
              ...item,
              found_at: f.found_at,
              found_id: f.found_id,
              found_photo_reference: f.found_photo_reference,
            };
          })
        );
      },
      (err) => {
        if (cancelled) return;
        console.warn('useCollectionDetail finds listener err:', err);
      }
    );

    void loadStaticParts();

    return () => {
      cancelled = true;
      findsUnsubRef.current?.();
      findsUnsubRef.current = null;
    };
  }, [collectionId, collectionRefPath]);

  return { detail, items, isLoading, error };
};
